import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { LuGauge, LuPlus, LuTrash2 } from "react-icons/lu";
import { api, ApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { SkeletonTable } from "@/components/ui/skeleton";

export function RateLimitsPage() {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["console", "admin", "rate-overrides"],
    queryFn: () => api.adminRateLimitOverrides.list(),
  });

  const [form, setForm] = useState({
    userId: "",
    resource: "inbox",
    overrideLimit: 50,
    reason: "",
    expiresAt: "",
  });

  const add = useMutation({
    mutationFn: () =>
      api.adminRateLimitOverrides.add({
        userId: form.userId.trim(),
        resource: form.resource.trim(),
        overrideLimit: form.overrideLimit,
        reason: form.reason || undefined,
        expiresAt: form.expiresAt ? new Date(form.expiresAt).toISOString() : null,
      }),
    onSuccess: () => {
      toast.success("Override saved.");
      setForm({ ...form, userId: "", reason: "", expiresAt: "" });
      qc.invalidateQueries({ queryKey: ["console", "admin", "rate-overrides"] });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : "Failed."),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.adminRateLimitOverrides.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["console", "admin", "rate-overrides"] }),
  });

  return (
    <>
      <PageHeader
        icon={LuGauge}
        title="Rate-limit overrides"
        description="Bump a specific user's per-resource limit temporarily. The product worker reads this table at request time."
      />

      <div className="p-8 space-y-6">
        <Card>
          <div className="px-5 py-3 border-b border-[var(--color-border)] flex items-center gap-2">
            <LuPlus className="w-4 h-4 text-[var(--color-text-subtle)]" />
            <p className="text-sm font-semibold text-[var(--color-text)]">Add override</p>
          </div>
          <div className="p-5 grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_2fr_1fr_auto] gap-3 items-end">
            <Field label="User id">
              <input
                value={form.userId}
                onChange={(e) => setForm({ ...form, userId: e.target.value })}
                placeholder="kE5b6qsGYK..."
                className="px-3 py-2 text-sm rounded-md bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text)] font-mono w-full focus:outline-none focus:border-[var(--color-accent)]"
              />
            </Field>
            <Field label="Resource">
              <select
                value={form.resource}
                onChange={(e) => setForm({ ...form, resource: e.target.value })}
                className="px-3 py-2 text-sm rounded-md bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text)] focus:outline-none focus:border-[var(--color-accent)] w-full"
              >
                <option>inbox</option>
                <option>extension</option>
                <option>api_call</option>
                <option>support_ticket</option>
              </select>
            </Field>
            <Field label="New limit">
              <input
                type="number"
                value={form.overrideLimit}
                onChange={(e) => setForm({ ...form, overrideLimit: Number(e.target.value) || 0 })}
                className="px-3 py-2 text-sm rounded-md bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text)] tabular-nums w-full focus:outline-none focus:border-[var(--color-accent)]"
              />
            </Field>
            <Field label="Reason">
              <input
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder="Demo for enterprise prospect"
                className="px-3 py-2 text-sm rounded-md bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text)] w-full focus:outline-none focus:border-[var(--color-accent)]"
              />
            </Field>
            <Field label="Expires (optional)">
              <input
                type="datetime-local"
                value={form.expiresAt}
                onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
                className="px-3 py-2 text-sm rounded-md bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text)] w-full focus:outline-none focus:border-[var(--color-accent)]"
              />
            </Field>
            <Button
              onClick={() => add.mutate()}
              disabled={!form.userId || add.isPending}
            >
              <LuPlus className="w-4 h-4" />
              Save
            </Button>
          </div>
        </Card>

        {list.isLoading && <SkeletonTable rows={4} cols={5} />}
        {list.data && (
          <Card>
            <table className="w-full text-sm">
              <thead className="bg-[var(--color-bg)]/40">
                <tr className="text-left text-[var(--color-text-subtle)] border-b border-[var(--color-border)]">
                  <th className="px-5 py-3 text-[11px] uppercase tracking-[0.12em]">User</th>
                  <th className="px-5 py-3 text-[11px] uppercase tracking-[0.12em]">Resource</th>
                  <th className="px-5 py-3 text-[11px] uppercase tracking-[0.12em] text-right">Limit</th>
                  <th className="px-5 py-3 text-[11px] uppercase tracking-[0.12em]">Reason</th>
                  <th className="px-5 py-3 text-[11px] uppercase tracking-[0.12em]">Expires</th>
                  <th className="px-5 py-3 w-12" />
                </tr>
              </thead>
              <tbody>
                {list.data.overrides.length === 0 && (
                  <tr><td colSpan={6} className="px-5 py-6 text-center text-sm text-[var(--color-text-muted)]">No active overrides.</td></tr>
                )}
                {list.data.overrides.map((o) => (
                  <tr key={o.id} className="border-b border-[var(--color-border)] last:border-0 hover:bg-[var(--color-elevated)]/30">
                    <td className="px-5 py-3">
                      <p className="text-[var(--color-text)] truncate max-w-[200px]">{o.user_email}</p>
                      <p className="text-[10px] text-[var(--color-text-subtle)] font-mono truncate max-w-[200px]">{o.user_id}</p>
                    </td>
                    <td className="px-5 py-3 font-mono text-xs text-[var(--color-accent-light)]">{o.resource}</td>
                    <td className="px-5 py-3 text-right font-mono tabular-nums text-[var(--color-text)]">{o.override_limit.toLocaleString()}</td>
                    <td className="px-5 py-3 text-xs text-[var(--color-text-muted)] truncate max-w-[280px]">{o.reason ?? "—"}</td>
                    <td className="px-5 py-3 text-xs font-mono text-[var(--color-text-subtle)] tabular-nums">
                      {o.expires_at ? new Date(o.expires_at).toLocaleDateString() : "never"}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => remove.mutate(o.id)}
                        className="text-[var(--color-text-subtle)] hover:text-[var(--color-error)] transition-colors p-1"
                        title="Remove"
                      >
                        <LuTrash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.12em] text-[var(--color-text-subtle)] mb-2">
        {label}
      </label>
      {children}
    </div>
  );
}
